import type { EmailTemplateDefinition, UpdateQueueSettingsInput } from '@easyhelp/shared';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef } from 'react';
import { useController, type Control } from 'react-hook-form';
import { insertPlaceholder } from './placeholders';

export interface EmailTemplateFieldsProps {
  template: EmailTemplateDefinition;
  control: Control<UpdateQueueSettingsInput>;
}

/**
 * Oggetto e corpo di un template di email automatica, con i segnaposto ammessi come chip:
 * un clic inserisce il segnaposto nel corpo, al cursore o in coda.
 */
export function EmailTemplateFields({ template, control }: EmailTemplateFieldsProps) {
  const subject = useController({ control, name: template.subjectField });
  const body = useController({ control, name: template.bodyField });
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingCaret = useRef<number | null>(null);

  useEffect(() => {
    const caret = pendingCaret.current;
    const textarea = bodyRef.current;
    if (caret === null || !textarea) return;
    pendingCaret.current = null;
    textarea.focus();
    textarea.setSelectionRange(caret, caret);
  }, [body.field.value]);

  const insert = (placeholder: string) => {
    const textarea = bodyRef.current;
    const { value, caret } = insertPlaceholder(
      body.field.value,
      placeholder,
      textarea?.selectionStart ?? null,
      textarea?.selectionEnd ?? null
    );
    pendingCaret.current = caret;
    body.field.onChange(value);
  };

  return (
    <Stack spacing={3} component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
      <Typography component="legend" variant="subtitle1" sx={{ fontWeight: 600, p: 0 }}>
        {template.label} · {template.recipient}
      </Typography>
      <TextField
        {...subject.field}
        label={`Oggetto · ${template.label}`}
        required
        fullWidth
        error={Boolean(subject.fieldState.error)}
        helperText={subject.fieldState.error?.message}
      />
      <TextField
        {...body.field}
        inputRef={(element: HTMLTextAreaElement | null) => {
          body.field.ref(element);
          bodyRef.current = element;
        }}
        label={`Testo · ${template.label}`}
        required
        fullWidth
        multiline
        minRows={4}
        error={Boolean(body.fieldState.error)}
        helperText={body.fieldState.error?.message}
      />
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          Segnaposto disponibili:
        </Typography>
        {template.placeholders.map((placeholder) => (
          <Chip
            key={placeholder}
            label={placeholder}
            size="small"
            variant="outlined"
            onClick={() => insert(placeholder)}
            aria-label={`Inserisci ${placeholder} nel testo · ${template.label}`}
          />
        ))}
      </Stack>
    </Stack>
  );
}
